import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, TrendingUp, Home, Wrench, Share2, Eye } from "lucide-react";
import { getProject, updateProject, getCostLibrary, trackEvent } from "@/lib/api";
import {
  estimatedRenovationCost, estimatedProfit, estimatedROI,
  totalProjectCost, costPerSqm, margin, formatCurrency, formatPct,
  estimatedInteriorCost, estimatedExteriorCost, estimatedSystemsCost,
  estimatedSoftCosts, estimatedExtensionsCost, extensionsAddedSqm, projectGLA,
} from "@/lib/calculations";
import { CurrencyProvider, CURRENCIES } from "@/lib/CurrencyContext";
import { PROJECT_GOALS, getGoal, buildGoalInsight, estimatedValueAdded } from "@/lib/projectGoals";
import { ACTUALS_ENABLED, parseRefId } from "@/lib/actuals";
import { toast } from "sonner";
import OverviewTab from "@/components/OverviewTab";
import RoomsTab from "@/components/RoomsTab";
import ExtensionsTab from "@/components/ExtensionsTab";
import ExteriorTab from "@/components/ExteriorTab";
import SystemsTab from "@/components/SystemsTab";
import SoftCostsTab from "@/components/SoftCostsTab";
import CostLibrary from "@/pages/CostLibrary";
import CalcHelp from "@/components/CalcHelp";
import ModeToggle from "@/components/ModeToggle";
import ActualsLog from "@/components/ActualsLog";
import ActualOverview from "@/components/ActualOverview";
import RoomPlanVsActual from "@/components/RoomPlanVsActual";
import ExtensionPlanVsActual from "@/components/ExtensionPlanVsActual";
import UserMenu from "@/components/UserMenu";
import DisclaimerBar from "@/components/DisclaimerBar";
import OnboardingBanner from "@/components/OnboardingBanner";
import ShareDialog from "@/components/ShareDialog";
import TrialBanner from "@/components/TrialBanner";
import TrialExpiredModal from "@/components/TrialExpiredModal";

const GOAL_ICON = { flip: TrendingUp, sell: Home, live: Wrench };

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "rooms", label: "Rooms" },
  { id: "extensions", label: "Extensions" },
  { id: "exterior", label: "Exterior" },
  { id: "systems", label: "Systems" },
  { id: "soft", label: "Soft Costs" },
  { id: "library", label: "Library" },
];

// SpaceCost is the room-level sister product — it never needs the
// whole-property tabs. We hide Exterior + Systems entirely and the
// OverviewTab strips out valuation/feasibility internally.
const SPACECOST_HIDDEN_TABS = new Set(["exterior", "systems"]);

const STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
];

export default function ProjectDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(null);  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [saving, setSaving] = useState(false);
  const [roomFilter, setRoomFilter] = useState(null);
  const [extFilter, setExtFilter] = useState(null);
  const [costLibrary, setCostLibrary] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  // Permission gates derived from the project payload.
  // 'owner' / 'edit' can write; 'view' is read-only.
  const permission = project?.permission || "owner";
  const canWrite = permission === "owner" || permission === "edit";
  const isOwner = permission === "owner";

  useEffect(() => {
    getCostLibrary(id).then(setCostLibrary).catch(() => setCostLibrary({}));
  }, []);

  const mode = ACTUALS_ENABLED && searchParams.get("mode") === "actual" ? "actual" : "estimate";
  const setMode = (m) => {
    const next = new URLSearchParams(searchParams);
    if (m === "actual") next.set("mode", "actual");
    else next.delete("mode");
    setSearchParams(next, { replace: true });
    // Library tab is hidden in actual mode — bounce back to overview if active.
    if (m === "actual" && tab === "library") setTab("overview");
    setRoomFilter(null);
    setExtFilter(null);
  };

  // Clear the per-room filter whenever the user leaves the Rooms tab.
  useEffect(() => {
    if (tab !== "rooms") setRoomFilter(null);
    if (tab !== "extensions") setExtFilter(null);
  }, [tab]);

  // SpaceCost projects don't have Exterior/Systems tabs — bounce back if a
  // legacy URL or stored tab tries to render them.
  useEffect(() => {
    if (project?.product === "spacecost" && SPACECOST_HIDDEN_TABS.has(tab)) {
      setTab("overview");
    }
  }, [project?.product, tab]);
  const saveTimer = useRef(null);
  const pendingPatch = useRef({});

  const loadProject = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getProject(id);
      setProject(data);
    } catch (e) {
      toast.error("Could not load project");
      navigate("/projects");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadProject(); }, [loadProject]);

  // Debounced save — accumulate patches so rapid edits aren't lost,
  // and merge (not replace) server response to preserve in-flight edits.
  const queueSave = useCallback((patch) => {
    if (!canWrite) return;  // view-only collaborators never trigger PUTs.
    pendingPatch.current = { ...pendingPatch.current, ...patch };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const toSend = pendingPatch.current;
      pendingPatch.current = {};
      try {
        setSaving(true);
        const saved = await updateProject(id, toSend);
        setProject((prev) => ({ ...prev, updated_at: saved.updated_at }));
      } catch (err) {
        // Trial paywall errors get a friendly toast pointing at the unlock CTA
        // rather than the generic "Save failed" message. The TrialBanner is
        // already mounted so the user just has to glance up.
        const detail = err?.response?.data?.detail;
        const code = detail?.code;
        if (code === "trial_room_cap_reached" || code === "trial_expired") {
          toast.error(detail.message || "Trial limit reached", { duration: 6000 });
          // Funnel signal — was it the 7-day expiry or the 1-room cap that
          // bit them? Admin Analytics tab splits these so we can see which
          // gate is converting better.
          trackEvent(code, `project:${id}`, { project_id: id });
        } else if (code === "spacecost_room_cap_reached") {
          toast.error(detail.message || "Room limit reached", { duration: 6000 });
        } else {
          toast.error("Save failed");
        }
      } finally {
        setSaving(false);
      }
    }, 450);
  }, [id, canWrite]);

  const updateField = (patch) => {
    setProject((prev) => {
      const next = { ...prev, ...patch };
      queueSave(patch);
      return next;
    });
  };

  const replaceRooms = (rooms) => {
    setProject((prev) => {
      const next = { ...prev, rooms };
      queueSave({ rooms });
      return next;
    });
  };

  const metrics = useMemo(() => {
    if (!project) return null;
    return {
      reno: estimatedRenovationCost(project),
      total: totalProjectCost(project),
      profit: estimatedProfit(project),
      roi: estimatedROI(project),
      costPerSqm: costPerSqm(project),
      margin: margin(project),
    };
  }, [project]);

  const goal = useMemo(() => getGoal(project?.project_goal), [project?.project_goal]);
  const GoalIcon = GOAL_ICON[goal.id] || TrendingUp;

  const metricCards = useMemo(() => {
    if (!project || !metrics) return [];
    const rooms = (project.rooms || []).length;
    const valueAdded = estimatedValueAdded(project, metrics);
    const fmt = (n) => formatCurrency(n, project.currency);
    const interior = estimatedInteriorCost(project);
    const exterior = estimatedExteriorCost(project);
    const systems = estimatedSystemsCost(project);
    const extensions = estimatedExtensionsCost(project);
    const addedSqm = extensionsAddedSqm(project);
    const gla = projectGLA(project);
    const soft = estimatedSoftCosts(project);
    const purchase = Number(project.purchase_price) || 0;
    const sale = Number(project.target_sale_price) || 0;
    const modelled = interior + exterior + systems + extensions;
    const renoHelp = {
      title: "Estimated renovation cost",
      formula: modelled > 0
        ? `Interior ${fmt(interior)} + Exterior ${fmt(exterior)} + Systems ${fmt(systems)}${extensions > 0 ? ` + Extensions ${fmt(extensions)}` : ""} = ${fmt(metrics.reno)}`
        : `${project.floor_area_sqm || 0} sqm × ${project.renovation_type} rate = ${fmt(metrics.reno)}`,
      lines: modelled > 0
        ? ["Live roll-up of every modelled room, exterior item, system, and extension."]
        : ["Heuristic fallback used when no rooms / exterior / systems are modelled yet. Switches to the modelled roll-up the moment you add items."],
    };
    const totalHelp = {
      title: "Total project cost",
      formula: `Purchase ${fmt(purchase)} + Reno ${fmt(metrics.reno)} + Soft ${fmt(soft)} = ${fmt(metrics.total)}`,
      lines: ["Every out-of-pocket cost combined. Soft costs include agent fees, holding, contingency and transfer."],
    };
    const profitHelp = {
      title: goal.metrics.profitLabel,
      formula: `Sale ${fmt(sale)} − Total ${fmt(metrics.total)} = ${fmt(metrics.profit)}`,
      lines: ["What remains after selling at the target price and paying every cost."],
    };
    const roiHelp = {
      title: goal.metrics.roiLabel,
      formula: `${fmt(metrics.profit)} ÷ ${fmt(metrics.total)} = ${formatPct(metrics.roi)}`,
      lines: ["Return on every dollar invested in the deal."],
    };
    const marginHelp = {
      title: goal.metrics.marginLabel,
      formula: `${fmt(metrics.profit)} ÷ ${fmt(sale)} = ${formatPct(metrics.margin)}`,
      lines: ["Share of the sale price retained after costs."],
    };
    const costSqmHelp = {
      title: "Renovation cost per sqm",
      formula: gla > 0
        ? `Reno ${fmt(metrics.reno)} ÷ ${gla} sqm${addedSqm > 0 ? ` (incl. +${addedSqm} ext.)` : ""} = ${fmt(metrics.reno / gla)}`
        : "Set a floor area on Overview to compute this.",
      lines: [
        "Renovation spend only — excludes purchase price and soft costs.",
        addedSqm > 0
          ? `Calculated against post-build GLA (${gla} sqm). Benchmark against comparable renovations in the same market.`
          : "Benchmark against comparable renovations in the same market.",
      ],
    };
    const valueAddedHelp = {
      title: "Estimated value added",
      formula: `Sale ${fmt(sale)} − Purchase ${fmt(purchase)} − Reno ${fmt(metrics.reno)} = ${fmt(valueAdded)}`,
      lines: ["Simple sanity check — how much extra value the renovation is expected to create before selling costs."],
    };
    const costPerRoomHelp = {
      title: "Cost per room",
      formula: rooms > 0 ? `Reno ${fmt(metrics.reno)} ÷ ${rooms} rooms = ${fmt(metrics.reno / rooms)}` : "Add rooms on the Rooms tab.",
      lines: ["Quick guide for prioritising where to spend next."],
    };
    const catalog = {
      reno: {
        tid: "metric-reno",
        label: "Est. Renovation",
        value: fmt(metrics.reno),
        sub: renoSubLabel(project),
        help: renoHelp,
      },
      total: {
        tid: "metric-total",
        label: "Total Project Cost",
        value: fmt(metrics.total),
        sub: "Purchase + Reno + Soft",
        help: totalHelp,
      },
      profit: {
        tid: "metric-profit",
        label: goal.metrics.profitLabel,
        value: fmt(metrics.profit),
        valueClass: metrics.profit >= 0 ? "text-[#059669]" : "text-[#DC2626]",
        sub: goal.metrics.profitSub,
        help: profitHelp,
      },
      roi: {
        tid: "metric-roi",
        label: goal.metrics.roiLabel,
        value: formatPct(metrics.roi),
        valueClass: metrics.roi >= 0 ? "text-[#059669]" : "text-[#DC2626]",
        sub: goal.metrics.roiSub,
        help: roiHelp,
      },
      margin: {
        tid: "metric-margin",
        label: goal.metrics.marginLabel,
        value: formatPct(metrics.margin),
        valueClass: metrics.margin >= 0 ? "text-[#059669]" : "text-[#DC2626]",
        sub: goal.metrics.marginSub,
        help: marginHelp,
      },
      costPerSqm: {
        tid: "metric-cost-per-sqm",
        label: "Reno cost / sqm",
        value: fmt(metrics.costPerSqm),
        sub: gla > 0
          ? `Renovation only · ${gla} sqm${addedSqm > 0 ? ` (incl. +${addedSqm} ext.)` : ""}`
          : "Set floor area to compute",
        help: costSqmHelp,
      },
      gla: {
        tid: "metric-gla",
        label: "GLA after build",
        value: `${gla} sqm`,
        sub: addedSqm > 0
          ? `${project.floor_area_sqm || 0} existing + ${addedSqm} new`
          : "No extensions yet",
        help: {
          title: "Gross Living Area",
          formula: `Existing ${project.floor_area_sqm || 0} sqm + Extensions ${addedSqm} sqm = ${gla} sqm`,
          lines: [
            "Total internal floor area once every extension is built.",
            "Drives the cost-per-sqm benchmark and resale-value uplift assumptions.",
          ],
        },
      },
      costPerRoom: {
        tid: "metric-cost-per-room",
        label: "Cost per Room",
        value: fmt(rooms > 0 ? metrics.reno / rooms : 0),
        sub: rooms > 0 ? `Across ${rooms} room${rooms === 1 ? "" : "s"}` : "Add rooms to compute",
        help: costPerRoomHelp,
      },
      valueAdded: {
        tid: "metric-value-added",
        label: "Est. Value Added",
        value: fmt(valueAdded),
        valueClass: valueAdded >= 0 ? "text-[#059669]" : "text-[#DC2626]",
        sub: "Sale − Purchase − Reno",
        help: valueAddedHelp,
      },
    };
    const orderedIds = [...(goal.emphasis || []), "reno", "total", "profit", "roi", "margin", "costPerSqm"];
    if (addedSqm > 0) orderedIds.push("gla");
    const seen = new Set();
    return orderedIds
      .filter((k) => catalog[k] && !seen.has(k) && seen.add(k))
      .map((k) => catalog[k]);
  }, [project, metrics, goal]);

  const insight = useMemo(
    () => (project && metrics ? buildGoalInsight(project, metrics, (n) => formatCurrency(n, project.currency)) : ""),
    [project, metrics]
  );

  if (loading || !project) {
    return (
      <div className="min-h-screen grid place-items-center text-[#A8A29E] text-sm">Loading…</div>
    );
  }

  return (
    <CurrencyProvider currency={project.currency}>
    <div
      className={`min-h-screen fc-app-bg text-[#27272A] fc-fade-in ${mode === "actual" ? "fc-mode-actual" : ""}`}
      data-testid="project-dashboard"
      data-mode={mode}
      data-theme={project.product === "spacecost" ? "spacecost" : undefined}
      data-product={project.product || "flipcost"}
    >
      {/* Sticky header */}
      <header className={`sticky top-0 z-30 fc-header-bar ${mode === "actual" ? "fc-header-actual" : ""}`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-start sm:items-center justify-between gap-2 sm:gap-4 flex-wrap">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 basis-[60%] sm:basis-auto">
            <button
              data-testid="back-to-projects-btn"
              onClick={() => navigate("/projects")}
              className="w-8 h-8 rounded-lg border border-black/[0.08] grid place-items-center text-[#78716C] hover:text-[#1C1C1C] hover:border-black/[0.2] transition-colors shrink-0"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="min-w-0 flex-1">
              <input
                data-testid="project-name-input"
                value={project.name}
                onChange={(e) => updateField({ name: e.target.value })}
                placeholder="Property Name"
                readOnly={!canWrite}
                className="fc-heading bg-transparent border-0 outline-none text-[#1C1C1C] text-base sm:text-lg font-medium w-full truncate focus:outline-none"
              />
              <input
                data-testid="project-address-input"
                value={project.address}
                onChange={(e) => updateField({ address: e.target.value })}
                placeholder="Address"
                readOnly={!canWrite}
                className="bg-transparent border-0 outline-none text-[#78716C] text-xs w-full truncate focus:outline-none"
              />
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-3 flex-wrap justify-end">
            {ACTUALS_ENABLED && <ModeToggle mode={mode} onChange={setMode} />}
            <div className="hidden sm:block text-[11px] text-[#A8A29E] min-w-[52px] text-right">
              {saving ? "Saving…" : "Saved"}
            </div>
            {isOwner && (
              <button
                type="button"
                data-testid="open-share-dialog-btn"
                onClick={() => setShareOpen(true)}
                title="Share this project with collaborators"
                aria-label="Share project"
                className="h-8 w-8 sm:w-auto sm:px-2.5 grid place-items-center sm:flex sm:items-center sm:gap-1.5 rounded-md border border-black/[0.08] hover:border-black/[0.25] bg-white hover:bg-[#F5F5F4] text-[11px] text-[#27272A] transition-colors"
              >
                <Share2 className="w-3.5 h-3.5 text-[#78716C]" />
                <span className="hidden sm:inline">Share</span>
              </button>
            )}
            <select
              data-testid="project-currency-select"
              value={project.currency || "USD"}
              onChange={(e) => updateField({ currency: e.target.value })}
              disabled={!canWrite}
              className="fc-input h-8 px-1.5 sm:px-2 text-xs rounded-md min-w-0 disabled:opacity-60 disabled:cursor-not-allowed"
              title="Project currency"
            >
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>{c.code}</option>
              ))}
            </select>
            {project.product !== "spacecost" && (
            <select
              data-testid="project-goal-select"
              value={project.project_goal || "flip"}
              onChange={(e) => updateField({ project_goal: e.target.value })}
              disabled={!canWrite}
              className="fc-input h-8 px-1.5 sm:px-2 text-xs rounded-md hidden md:block disabled:opacity-60 disabled:cursor-not-allowed"
              title="Project goal"
            >
              {PROJECT_GOALS.map((g) => (
                <option key={g.id} value={g.id}>{g.label}</option>
              ))}
            </select>
            )}
            <select
              data-testid="project-status-select"
              value={project.status}
              onChange={(e) => updateField({ status: e.target.value })}
              disabled={!canWrite}
              className="fc-input h-8 px-1.5 sm:px-2 text-xs rounded-md hidden md:block disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <UserMenu />
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 flex gap-1 overflow-x-auto fc-no-scrollbar" data-testid="dashboard-tabs">
          {TABS.filter((t) => project.product !== "spacecost" || !SPACECOST_HIDDEN_TABS.has(t.id)).map((t) => {
            const disabled = mode === "actual" && t.id === "library";
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => !disabled && setTab(t.id)}
                disabled={disabled}
                title={disabled ? "This tab is plan-only — switch to Estimate" : undefined}
                className={`px-3 py-2.5 text-sm transition-colors whitespace-nowrap ${
                  tab === t.id && !disabled
                    ? "fc-tab-active"
                    : disabled
                    ? "text-[#D6D3D1] cursor-not-allowed border-b-2 border-transparent"
                    : "text-[#78716C] hover:text-[#1C1C1C] border-b-2 border-transparent"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </header>

      {/* Read-only banner for shared viewers */}
      {!canWrite && (
        <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-5" data-testid="readonly-banner">
          <div className="rounded-lg bg-sage-50 border border-sage-200 text-sage-700 px-4 py-3 text-sm flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-medium bg-sage-100 border border-sage-200 rounded-full px-2 py-0.5">
              <Eye className="w-3 h-3" /> View only
            </span>
            <span className="text-sage-700/90">
              You've been given read-only access to this project. Edits won't be saved. Ask the owner to grant you <strong>Can edit</strong> if you need to make changes.
            </span>
          </div>
        </div>
      )}

      {/* FlipCost trial banner — only renders when project.trial === true.
          Surfaces remaining days + the unlock CTA so the user knows exactly
          where they sit in the funnel. */}
      <TrialBanner project={project} />

      {/* One-shot trial-ended modal — fires the very first time the user
          opens an expired trial project (deduped via localStorage). */}
      <TrialExpiredModal project={project} />

      {/* Actual-mode banner */}
      {mode === "actual" && (
        <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-5" data-testid="actual-mode-banner">
          <div className="rounded-lg bg-slate-brand-50 border border-slate-brand-200 text-slate-brand-700 px-4 py-3 text-sm flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-medium bg-slate-brand-100 border border-slate-brand-200 rounded-full px-2 py-0.5">
              Actual mode
            </span>
            <span className="text-slate-brand-700/90">
              Tracking what you've actually spent. Switch to <strong>Estimate</strong> any time to adjust the plan.
            </span>
          </div>
        </div>
      )}

      {/* Onboarding "Next step" banner — Estimate mode only. SpaceCost
          projects are simple enough to skip the FlipCost onboarding. */}
      {mode === "estimate" && project.product !== "spacecost" && (
        <OnboardingBanner
          project={project}
          onSetTab={setTab}
          onReviewLibrary={() => setTab("library")}
          onChangeCurrency={(c) => updateField({ currency: c })}
          onAcknowledgeCurrency={() => {
            const el = document.querySelector('[data-testid="project-currency-select"]');
            if (el) {
              try { el.focus(); } catch (_e) {}
              el.classList.add("ring-2", "ring-sage-700", "ring-offset-2");
              setTimeout(() => {
                el.classList.remove("ring-2", "ring-sage-700", "ring-offset-2");
              }, 1800);
            }
          }}
        />
      )}

      {/* Summary cards — Overview tab in plan mode only. SpaceCost is
          budget-only (no ARV / profit / ROI), so we skip the strap. */}
      {mode === "estimate" && tab === "overview" && project.product !== "spacecost" && (
      <section className="max-w-7xl mx-auto px-3 sm:px-6 pt-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4" data-testid="project-goal-strap">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full fc-goal-pill text-[11px]">
                <GoalIcon className="w-3 h-3" />
                <span className="font-medium">{goal.label}</span>
              </span>
            </div>
            <h2 className="fc-heading text-2xl text-[#1C1C1C] mt-3" data-testid="dashboard-headline">
              {goal.dashboardHeadline}
            </h2>
            <p className="text-sm text-[#78716C] mt-1 max-w-2xl">{goal.dashboardSub}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="summary-cards">
          {metricCards.slice(0, 4).map((c) => (
            <SummaryCard key={c.tid} {...c} />
          ))}
        </div>
        {metricCards.length > 4 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4" data-testid="summary-cards-secondary">
            {metricCards.slice(4, 6).map((c) => (
              <SummaryCard key={c.tid} {...c} />
            ))}
          </div>
        )}

        {(project.currency && project.currency !== "USD") && (
          <p
            className="text-[11px] text-[#78716C] italic mt-3"
            data-testid="currency-approx-note"
          >
            Currency approx. — default rates are scaled from USD by a regional multiplier.
            Save local rates in the <button
              type="button"
              onClick={() => setTab("library")}
              className="underline hover:text-[#1C1C1C]"
            >Cost Library</button> for project-specific accuracy.
          </p>
        )}

        {insight && (
          <div
            className="fc-card mt-4 p-5 border-l-4 bg-sage-50"
            style={{ borderLeftColor: "var(--fc-sage-700)" }}
            data-testid="goal-insight-card"
          >
            <div className="fc-label">Summary</div>
            <p className="text-[15px] text-[#1C1C1C] leading-relaxed mt-1.5" data-testid="goal-insight-text">
              {insight}
            </p>
          </div>
        )}
      </section>
      )}

      {/* Tab content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
        {mode === "estimate" ? (
          <>
            {tab === "overview" && (
              <OverviewTab project={project} onChange={updateField} />
            )}
            {tab === "rooms" && (
              <RoomsTab project={project} onRoomsChange={replaceRooms} costLibrary={costLibrary} />
            )}
            {tab === "extensions" && (
              <ExtensionsTab project={project} onChange={updateField} costLibrary={costLibrary} />
            )}
            {tab === "exterior" && (
              <ExteriorTab project={project} onChange={updateField} costLibrary={costLibrary} />
            )}
            {tab === "systems" && (
              <SystemsTab project={project} onChange={updateField} costLibrary={costLibrary} />
            )}
            {tab === "soft" && (
              <SoftCostsTab project={project} onChange={updateField} />
            )}
            {tab === "library" && (
              <CostLibrary
                projectId={project.id}
                project={project}
                onSaved={(saved) => setCostLibrary(saved)}
              />
            )}
          </>
        ) : (
          <>
            {tab === "overview" && <ActualOverview project={project} />}
            {tab === "rooms" && (
              <ActualsLog
                project={project}
                onChange={updateField}
                category="interior"
                plannedTotal={estimatedInteriorCost(project)}
                title="Interior spend"
                description="Track every receipt that relates to rooms and interior finishes. Entries roll up to the Interior budget on Overview."
                topStrip={
                  <RoomPlanVsActual
                    project={project}
                    activeRoomId={roomFilter}
                    onToggleRoom={setRoomFilter}
                  />
                }
                extraFilter={
                  roomFilter
                    ? (entry) => {
                        const ref = parseRefId(entry.ref_id);
                        const knownRoomIds = new Set((project.rooms || []).map((r) => r.id));
                        if (roomFilter === "__unassigned__") {
                          return !ref || ref.kind !== "room" || !knownRoomIds.has(ref.roomId);
                        }
                        return ref && ref.kind === "room" && ref.roomId === roomFilter;
                      }
                    : null
                }
              />
            )}
            {tab === "exterior" && (
              <ActualsLog
                project={project}
                onChange={updateField}
                category="exterior"
                plannedTotal={estimatedExteriorCost(project)}
                title="Exterior spend"
                description="Roof, paint, paving, landscaping, windows & doors, boundary walls, security."
              />
            )}
            {tab === "extensions" && (
              <ActualsLog
                project={project}
                onChange={updateField}
                category="extensions"
                plannedTotal={estimatedExtensionsCost(project)}
                title="Extensions spend"
                description="Shell costs (foundation, structure, services) and any rooms inside extensions. Allocate each receipt to a specific extension and item."
                topStrip={
                  <ExtensionPlanVsActual
                    project={project}
                    activeExtId={extFilter}
                    onToggleExt={setExtFilter}
                  />
                }
                extraFilter={
                  extFilter
                    ? (entry) => {
                        const ref = parseRefId(entry.ref_id);
                        const knownExtIds = new Set((project.extensions || []).map((e) => e.id));
                        if (extFilter === "__unassigned__") {
                          return !ref || ref.kind !== "ext" || !knownExtIds.has(ref.extId);
                        }
                        return ref && ref.kind === "ext" && ref.extId === extFilter;
                      }
                    : null
                }
              />
            )}
            {tab === "systems" && (
              <ActualsLog
                project={project}
                onChange={updateField}
                category="systems"
                plannedTotal={estimatedSystemsCost(project)}
                title="Systems spend"
                description="Electrical, plumbing, hot water, solar, flooring (global), ceilings, waterproofing, structural."
              />
            )}
            {tab === "soft" && (
              <ActualsLog
                project={project}
                onChange={updateField}
                category="soft"
                plannedTotal={estimatedSoftCosts(project)}
                title="Soft cost spend"
                description="Fees, holding costs, commission, compliance, admin."
              />
            )}
          </>
        )}
        <DisclaimerBar />
      </main>
      {isOwner && (
        <ShareDialog open={shareOpen} onOpenChange={setShareOpen} project={project} />
      )}
    </div>
    </CurrencyProvider>
  );
}

function renoSubLabel(project) {
  const rooms = (project.rooms || []).length;
  const extEnabled = (project.exterior || []).filter((e) => e.enabled).length;
  const sysEnabled = (project.systems || []).filter((e) => e.enabled).length;
  const parts = [];
  if (rooms > 0) parts.push(`${rooms} room${rooms === 1 ? "" : "s"}`);
  if (extEnabled > 0) parts.push(`${extEnabled} ext.`);
  if (sysEnabled > 0) parts.push(`${sysEnabled} sys.`);
  if (parts.length === 0) return "Heuristic (area × scope)";
  return `Modelled: ${parts.join(", ")}`;
}

function SummaryCard({ tid, label, value, sub, valueClass = "text-[#1C1C1C]", help = null }) {
  return (
    <div className="fc-card p-5 relative" data-testid={tid}>
      {help && (
        <div className="absolute top-3 right-3">
          <CalcHelp
            testId={`${tid}-help`}
            title={help.title}
            formula={help.formula}
            lines={help.lines}
          />
        </div>
      )}
      <div className="fc-label pr-6">{label}</div>
      <div className={`fc-heading text-3xl tracking-tight mt-2 ${valueClass}`}>{value}</div>
      {sub && <div className="text-xs text-[#A8A29E] mt-2">{sub}</div>}
    </div>
  );
}
